import m from '../m.js'
import { toasts, upload } from '../api.js'
import { drafts } from '../drafts.js'
import { icon } from '../icons.js'
import {
  DEFMOD_LABEL,
  keyBindingsView,
  PASS_KEYPRESS_TO_OUTER_SCOPE,
} from '../keyBindings.js'
import { renderMarkdown } from '../markdown.js'
import { htmlView } from './widgets.js'

/**
 * Markdown textarea with Write/Preview tabs and image upload by paste, drop or picker.
 * attrs: value, onsave(text) → Promise, oncancel?, saveLabel, placeholder, clearOnSave
 */
let editors = 0
let uploads = 0

export const editorView = m.withState(initial => {
  // `draftKey`: the text is kept in the browser until saved or cancelled (see drafts.js)
  const draftKey = initial.draftKey
  const original = initial.value || ''
  let text = drafts.get(draftKey) ?? original
  let stored = text
  function persist() {
    if (text === stored) return
    stored = text
    if (text === original) drafts.remove(draftKey)
    else drafts.set(draftKey, text)
  }
  function cancel(attrs) {
    drafts.remove(draftKey)
    attrs.oncancel()
  }
  let tab = 'write'
  let uploading = 0
  let saving = false
  let textarea

  function insert(snippet) {
    const start = textarea?.selectionStart ?? text.length
    const end = textarea?.selectionEnd ?? text.length
    const pad = start > 0 && !text.slice(0, start).endsWith('\n') ? '\n' : ''
    text = text.slice(0, start) + pad + snippet + text.slice(end)
    m.redraw.sync()
    const caret = start + pad.length + snippet.length
    textarea?.setSelectionRange(caret, caret)
  }

  /** Puts the text between the markers around the selection and keeps it selected. */
  function wrap(before, after = before, placeholder = '') {
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selected = text.slice(start, end) || placeholder
    text = text.slice(0, start) + before + selected + after + text.slice(end)
    m.redraw.sync()
    textarea.focus()
    textarea.setSelectionRange(
      start + before.length,
      start + before.length + selected.length
    )
  }

  /** Prefixes every line touched by the selection, e.g. "> " or "1. ". */
  function prefixLines(prefix) {
    const start = text.lastIndexOf('\n', textarea.selectionStart - 1) + 1
    let end = text.indexOf('\n', textarea.selectionEnd)
    if (end < 0) end = text.length
    const lines = text
      .slice(start, end)
      .split('\n')
      .map(
        (line, i) => (typeof prefix === 'function' ? prefix(i) : prefix) + line
      )
    text = text.slice(0, start) + lines.join('\n') + text.slice(end)
    m.redraw.sync()
    textarea.focus()
    textarea.setSelectionRange(start, start + lines.join('\n').length)
  }

  /**
   * Enter in a list line starts the next item (numbers count up, tasks start unchecked);
   * on an empty item it ends the list instead, like GitHub. Returns whether it handled Enter.
   */
  function continueList() {
    const pos = textarea.selectionStart
    if (pos !== textarea.selectionEnd) return false
    const lineStart = text.lastIndexOf('\n', pos - 1) + 1
    const match = text
      .slice(lineStart, pos)
      .match(/^(\s*)(?:([-*+]) \[[ xX]\] |([-*+]) |(\d+)([.)]) )/)
    if (!match) return false
    const [marker, indent, task, bullet, number, separator] = match
    const lineEnd =
      text.indexOf('\n', pos) < 0 ? text.length : text.indexOf('\n', pos)
    let caret
    if (!text.slice(lineStart + marker.length, lineEnd).trim()) {
      text = text.slice(0, lineStart) + text.slice(lineStart + marker.length)
      caret = lineStart
    } else {
      const next =
        indent +
        (task
          ? `${task} [ ] `
          : bullet
            ? `${bullet} `
            : `${Number(number) + 1}${separator} `)
      text = `${text.slice(0, pos)}\n${next}${text.slice(pos)}`
      caret = pos + 1 + next.length
    }
    m.redraw.sync()
    textarea.setSelectionRange(caret, caret)
    return true
  }

  function link() {
    const start = textarea.selectionStart
    const label = text.slice(start, textarea.selectionEnd)
    wrap('[', '](url)')
    // select "url", ready to paste
    const url = start + 1 + label.length + 2
    textarea.setSelectionRange(url, url + 3)
  }

  const code = () =>
    text.slice(textarea.selectionStart, textarea.selectionEnd).includes('\n')
      ? wrap('```\n', '\n```')
      : wrap('`')

  // GitHub's markdown toolbar; `key` is the Cmd/Ctrl shortcut
  const TOOLS = [
    { icon: 'heading', hint: 'editor.heading', run: () => prefixLines('### ') },
    { icon: 'bold', hint: 'editor.bold', key: 'b', run: () => wrap('**') },
    { icon: 'italic', hint: 'editor.italic', key: 'i', run: () => wrap('_') },
    { icon: 'quote', hint: 'editor.quote', run: () => prefixLines('> ') },
    { icon: 'code', hint: 'editor.code', key: 'e', run: code },
    { icon: 'link', hint: 'editor.link', key: 'k', run: link },
    null,
    {
      icon: 'list-unordered',
      hint: 'editor.bullets',
      run: () => prefixLines('- '),
    },
    {
      icon: 'list-ordered',
      hint: 'editor.numbers',
      run: () => prefixLines(i => `${i + 1}. `),
    },
    {
      icon: 'tasklist',
      hint: 'editor.tasks',
      run: () => prefixLines('- [ ] '),
    },
  ]
  const hint = tool =>
    m.t(tool.hint) +
    (tool.key ? ` (${DEFMOD_LABEL}${tool.key.toUpperCase()})` : '')

  /**
   * Inserts a placeholder at the caret, replaced by the image once uploaded. The number
   * keeps placeholders apart (pasted screenshots are all "image.png"); if the user removed
   * the placeholder meanwhile, the image is appended instead of being lost.
   */
  function uploadImage(file) {
    const name = file.name || 'image'
    const placeholder = `![Uploading ${name}… (${++uploads})]()`
    insert(`${placeholder}\n`)
    uploading++
    upload(file)
      .then(({ url }) => {
        const image = `![${name.replace(/[[\]]/g, '')}](${url})`
        if (text.includes(placeholder)) text = text.replace(placeholder, image)
        else text += `${text && !text.endsWith('\n') ? '\n' : ''}${image}\n`
      })
      .catch(err => {
        text = text.replace(`${placeholder}\n`, '').replace(placeholder, '')
        toasts.show(err.message)
      })
      .finally(() => {
        uploading--
        m.redraw()
      })
  }

  function attach(files) {
    for (const file of files) {
      if (file.type.startsWith('image/')) uploadImage(file)
      else toasts.show(m.t('editor.notImage', { name: file.name }))
    }
  }

  async function save(attrs) {
    if (uploading || saving) return
    saving = true
    try {
      await attrs.onsave(text)
      drafts.remove(draftKey)
      stored = text
      if (attrs.clearOnSave) {
        text = ''
        tab = 'write'
      }
    } catch {
      // error already shown as toast; keep the text
    } finally {
      saving = false
      m.redraw()
    }
  }

  // several editors can be open: each one's bindings act only while its textarea has focus
  let current
  const scope = `editor-${++editors}`
  const own = fn => evt =>
    document.activeElement === textarea ? fn(evt) : PASS_KEYPRESS_TO_OUTER_SCOPE
  const bindings = {
    ...Object.fromEntries(
      TOOLS.filter(tool => tool?.key).map(tool => [
        `defmod-${tool.key}`,
        own(() => {
          tool.run()
        }),
      ])
    ),
    'defmod-enter': own(() => {
      save(current)
    }),
    // returning false keeps the browser's newline
    enter: own(evt => !evt.isComposing && continueList()),
    // first Esc leaves the editor, a second one closes the detail view
    esc: own(() => (current.oncancel ? cancel(current) : textarea.blur())),
  }

  // stored after every redraw: typing, toolbar, list continuation and uploads all change `text`
  return attrs => {
    current = attrs
    return m.m('.editor', { onRender: persist }, [
      tab === 'write' &&
        keyBindingsView(scope, bindings, { skipInInputs: false }),
      m('.editor-box', [
        m('.editor-tabs', [
          m.m(
            'button',
            {
              type: 'button',
              cls: { active: tab === 'write' },
              title: m.t('editor.writeHint'),
              onclick: () => (tab = 'write'),
            },
            m.t('editor.write')
          ),
          m.m(
            'button',
            {
              type: 'button',
              cls: { active: tab === 'preview' },
              title: m.t('editor.previewHint'),
              onclick: () => (tab = 'preview'),
            },
            m.t('editor.preview')
          ),
          m('.spacer'),
          tab === 'write' &&
            m(
              '.editor-toolbar',
              TOOLS.map(tool =>
                tool
                  ? m(
                      'button.icon-button',
                      {
                        type: 'button',
                        title: hint(tool),
                        'aria-label': hint(tool),
                        onclick: tool.run,
                      },
                      icon(tool.icon)
                    )
                  : m('span.toolbar-divider')
              )
            ),
        ]),
        tab === 'write'
          ? m('textarea', {
              oncreate: v => {
                textarea = v.dom
                if (attrs.autofocus) textarea.focus()
              },
              value: text,
              placeholder: attrs.placeholder || m.t('editor.placeholder'),
              rows: attrs.rows || 8,
              oninput: evt => (text = evt.target.value),
              onpaste: evt => {
                const files = [...evt.clipboardData.files]
                if (files.length) {
                  evt.preventDefault()
                  attach(files)
                } else evt.redraw = false
              },
              ondragover: evt => {
                if (evt.dataTransfer.types.includes('Files'))
                  evt.preventDefault()
                evt.redraw = false
              },
              ondrop: evt => {
                if (!evt.dataTransfer.files.length) return
                evt.preventDefault()
                attach([...evt.dataTransfer.files])
              },
            })
          : m(
              '.editor-preview',
              htmlView({
                html: text.trim() && renderMarkdown(text),
                empty: m.t('editor.nothing'),
              })
            ),
      ]),
      m('.editor-actions', [
        m('label.attach', { title: m.t('editor.attachHint') }, [
          icon('paperclip'),
          uploading > 0
            ? m.t('editor.uploading', { n: uploading })
            : m.t('editor.attach'),
          m('input[type=file][accept=image/*][multiple][hidden]', {
            onchange: evt => {
              attach([...evt.target.files])
              evt.target.value = ''
            },
          }),
        ]),
        m('.spacer'),
        attrs.actions,
        attrs.oncancel &&
          m(
            'button',
            {
              type: 'button',
              title: m.t('editor.cancelHint'),
              onclick: () => cancel(attrs),
            },
            m.t('editor.cancel')
          ),
        m(
          'button.primary',
          {
            title: m.t('editor.saveHint'),
            disabled:
              uploading > 0 || saving || (attrs.requireText && !text.trim()),
            onclick: () => save(attrs),
          },
          saving ? m.t('editor.saving') : attrs.saveLabel || m.t('editor.save')
        ),
      ]),
    ])
  }
})
